// Moved from `src/hooks/mutation/useMutateCheckoutV2.ts` (now a delegate).
//
// The body that stayed behind for three grading passes was not one thing: it
// was a request with app effects wrapped round every side of it. What crosses
// here is the request — which endpoint, what body, what to do with the URL,
// and the link-accounts retry. What stays app-side is every effect, each one
// fired through a port at the line it fired on before:
//
//   Apple IAP guard   the CALL to `isAppleCompliantBuild()` stays in the
//                     delegate (CLAUDE.md: guard at the component/call site,
//                     visible in the diff). Only the boolean crosses. The
//                     `logger.warn` beside it moves in with it — delivery §7a
//                     rules logging not an app effect, and `@heidi/runtime/logger`
//                     is importable from `/data`.
//   cookies           `onBeforeCheckout` / `onCheckoutError`; the `!isTauri()`
//                     and `postCheckoutSuccessRedirectUrl` conditions go with
//                     them, so the ports fire unconditionally here and the
//                     delegate decides.
//   rewardful         `resolveReferral` — a vendor global read off `window`.
//   amplitude         `resolveAttribution`; device/session ids are written
//                     INTO the request body, so they are resolved before the
//                     body is assembled, not observed after it.
//   tauri + router    `resolveFeEndpoint` and `onCheckoutUrl`.
//   analytics         `onReferralCouponApplied` / `onCheckoutStarted`.
//   toast + intl      `onCheckoutFailed`.
//
// THREE OF THE INPUTS ARE FUNCTIONS, NOT VALUES, and that is a correctness
// choice rather than a style one. All three legacy reads happened INSIDE
// `mutationFn`, at click time; a delegate can only pass a value it read at
// RENDER time, and the gap between the two is not theoretical here. The
// Rewardful script loads asynchronously, so a paywall that renders before it
// lands would send an empty `client_reference_id` and silently lose the
// referral attribution; an Amplitude session rolls over after inactivity, so
// a modal left open would stamp the body with a session that has ended. Each
// resolver is called at the exact line its legacy expression sat on.
//
// `linkAccounts` is INJECTED rather than carved, measured by route: it posts
// `users/linked-account`, a user-identity resource, not a billing one. See the
// prop doc below.

import { logger } from '@heidi/runtime/logger';
import { useMutation } from '@tanstack/react-query';
import { type Period, type SubscriptionTier } from '../../contracts';
import { createCheckoutSession, createOpenApiCheckoutSession } from '../api/subscription';

export type UseMutateCheckoutV2VariablesBase = {
  fe_endpoint: string;
  fe_return_endpoint: string;
  free_trial_days?: number;
  kindeUserId?: string;
  postCheckoutSuccessRedirectUrl?: string;
};

/** Tier checkout — the legacy path. */
export type UseMutateCheckoutV2TierVariables = UseMutateCheckoutV2VariablesBase & {
  plan_period: Period;
  tier: SubscriptionTier;
};

/**
 * Clinician Max checkout — `price_id` only on the wire (GRO-4369).
 * `plan_period` is analytics-only; interval comes from the resolved price.
 */
export type UseMutateCheckoutV2PriceIdVariables = UseMutateCheckoutV2VariablesBase & {
  price_id: string;
  plan_period: Period;
};

export type UseMutateCheckoutV2Variables =
  | UseMutateCheckoutV2TierVariables
  | UseMutateCheckoutV2PriceIdVariables;

/**
 * The error envelope the link-accounts retry keys on. Structural rather than
 * `AxiosError`, because this package's public signatures never name axios
 * (V3 rule 3) — and the only field the branch reads is the discriminator.
 */
type CheckoutError = {
  response?: { data?: { detail?: { type?: string } } };
};

/** The one-shot retry's trigger, exported so both suites can name it. */
export const LINKED_ACCOUNT_NOT_FOUND = 'ErrOpenAPILinkedAccountNotFound';

export type UseMutateCheckoutV2Props = {
  /**
   * Widget launches hold an integration-auth token and check out through the
   * OpenAPI session endpoint. Resolved app-side from the integration slices
   * (`useCheckoutRequestContext`).
   */
  useOpenApiCheckout: boolean;
  /** Integrations do NOT need a credit card when starting a trial. */
  ccRequired: boolean;
  /**
   * `isAppleCompliantBuild()`, resolved app-side. The GUARD CALL stays at the
   * delegate on purpose: CLAUDE.md requires the Apple suppression to be
   * visible in the same file as the billing CTA path it suppresses, and a
   * boolean crossing a door is not that. Only the decision crosses.
   */
  checkoutSuppressed: boolean;
  /** `getRewardfulReferral()`. Called where the legacy destructure stood. */
  resolveReferral: () => { referralId?: string; couponId?: string };
  /** `amplitude.getDeviceId()` / `.getSessionId()`, each `?? null`. */
  resolveAttribution: () => {
    amplitudeDeviceId: string | null;
    /** Amplitude session ids are epoch-millisecond NUMBERS, not strings. */
    amplitudeSessionId: number | null;
  };
  /**
   * `isTauri() ? tauriStripeRedirectUrl() : feEndpoint`, owned by the delegate.
   * A resolver rather than a plain string for a second reason on top of the
   * click-time one above: the non-Tauri arm is the caller's own `fe_endpoint`
   * mutation variable, which does not exist when the hook is called.
   */
  resolveFeEndpoint: (feEndpoint: string) => string;
  /**
   * POST `users/linked-account`. INJECTED, NOT CARVED, and the reason is the
   * route: `users/linked-account` is a user-identity resource owned by
   * `workspace/identity-projection`, not a billing one, so carving it into
   * `packages/commerce/src/data/api` would claim another domain's endpoint to
   * save an argument. Its sibling on the same module, `payments/checkout`,
   * measured the other way and did move.
   */
  linkAccounts: (kindeUserId: string) => Promise<unknown>;
  /**
   * Fires at the top of `mutationFn`, before the request. The delegate sets
   * the post-checkout redirect cookie here, under its own `!isTauri() &&
   * input.postCheckoutSuccessRedirectUrl` condition.
   */
  onBeforeCheckout?: (input: UseMutateCheckoutV2Variables) => void;
  /** Fires only when BOTH referral values are present, as the legacy branch did. */
  onReferralCouponApplied?: (referral: { referralId: string; couponId: string }) => void;
  /** Fires where the tauri-open / router-push branch stood. */
  onCheckoutUrl?: (checkoutSessionUrl: string) => void;
  /**
   * Fires where the "couldn't start checkout" toast fired — BOTH of its
   * positions: the no-URL response and the non-link-account error path. One
   * port for two lines because the two lines are the same effect with the same
   * copy; splitting them would invent a distinction the legacy code never made.
   */
  onCheckoutFailed?: () => void;
  /** `onSuccess` — the paywall checkout event. */
  onCheckoutStarted?: (planPeriod: Period) => void;
  /**
   * Fires at the top of `onError` for EVERY error, before the link-account
   * branch — the position the redirect-cookie clear held. It is not the toast:
   * the toast is `onCheckoutFailed` and fires only on the non-link path.
   */
  onCheckoutError?: (input: UseMutateCheckoutV2Variables) => void;
};

export function useMutateCheckoutV2({
  useOpenApiCheckout,
  ccRequired,
  checkoutSuppressed,
  resolveReferral,
  resolveAttribution,
  resolveFeEndpoint,
  linkAccounts,
  onBeforeCheckout,
  onReferralCouponApplied,
  onCheckoutUrl,
  onCheckoutFailed,
  onCheckoutStarted,
  onCheckoutError,
}: UseMutateCheckoutV2Props) {
  const mutation = useMutation({
    mutationKey: ['postCheckoutV2'],
    mutationFn: async (input: UseMutateCheckoutV2Variables) => {
      if (checkoutSuppressed) {
        logger.warn('[compliance] Checkout suppressed on Apple-compliant build');
        return;
      }

      onBeforeCheckout?.(input);

      const { referralId, couponId } = resolveReferral();
      if (referralId && couponId) {
        logger.debug('referralId:', referralId);
        logger.debug('couponId:', couponId);
        onReferralCouponApplied?.({
          referralId,
          couponId,
        });
      }

      const checkoutFn = useOpenApiCheckout
        ? createOpenApiCheckoutSession
        : createCheckoutSession;

      const { amplitudeDeviceId, amplitudeSessionId } = resolveAttribution();

      const checkoutRequestData =
        'price_id' in input
          ? {
              price_id: input.price_id,
              fe_endpoint: resolveFeEndpoint(input.fe_endpoint),
              fe_return_endpoint: input.fe_return_endpoint,
              client_reference_id: referralId,
              coupon_id: couponId,
              cc_required: ccRequired,
              amplitude_device_id: amplitudeDeviceId,
              amplitude_session_id: amplitudeSessionId,
            }
          : {
              period: input.plan_period,
              tier: input.tier,
              fe_endpoint: resolveFeEndpoint(input.fe_endpoint),
              fe_return_endpoint: input.fe_return_endpoint,
              client_reference_id: referralId,
              coupon_id: couponId,
              free_trial_days: input.free_trial_days,
              cc_required: ccRequired, // integrations DO NOT need CC when starting a trial
              amplitude_device_id: amplitudeDeviceId,
              amplitude_session_id: amplitudeSessionId,
            };

      const res = await checkoutFn(checkoutRequestData);
      logger.debug('res', res);

      if (res?.data.checkout_session_url) {
        onCheckoutUrl?.(res?.data.checkout_session_url);
      } else {
        onCheckoutFailed?.();
      }
    },
    onSuccess: (_, input) => {
      // tracking
      onCheckoutStarted?.(input.plan_period);
    },
    onError: async (error: CheckoutError, input) => {
      logger.error('Error:', error);

      onCheckoutError?.(input);

      if (
        error.response?.data?.detail?.type === LINKED_ACCOUNT_NOT_FOUND &&
        input.kindeUserId
      ) {
        await linkAccounts(input.kindeUserId);

        mutation.mutate(input);
      } else {
        onCheckoutFailed?.();
      }
    },
  });

  return mutation;
}
